import { COACH_MARKETPLACE_PAGE } from 'constants/pages';
import { CURRENCY_USD } from 'constants/currency';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import { CoachSports, SportsEnum } from 'types/generated/client';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { ButtonLink } from 'components/Button';
import ExploreLessonCard from 'components/Explore/Lessons/ExploreLessonCard';
import LoadingSkeleton from 'components/LoadingSkeleton';
import SectionDivider from 'screens/ClubOverview/components/SectionDivider';
import SectionEmptyState from '../SectionEmptyState';
import { useVenueCoaches } from './useVenueCoaches';

interface Props {
  venueId: string;
}

export default function VenueCoaches({ venueId }: Props) {
  const { coaches, isLoading } = useVenueCoaches({ venueId });

  return (
    <>
      <SectionDivider className="my-ds-3xl" />
      <section className="flex flex-col gap-ds-xl">
      <h2 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        Coaches ({coaches.length})
      </h2>
      {isLoading ? (
        <LoadingSkeleton
          count={2}
          height="20rem"
          borderRadius="0.5rem"
          containerClassName="grid grid-cols-1 gap-ds-lg sm:grid-cols-2"
        />
      ) : coaches.length === 0 ? (
        <SectionEmptyState
          message="No coaches at this club yet"
          hint="Stay tuned — new coaches are on the way."
          iconSrc="/images/app/cap-colored.png"
        />
      ) : (
        <div className="grid grid-cols-1 gap-ds-lg sm:grid-cols-2">
          {coaches.map((coach) => (
            <div key={coach.id} className="w-full overflow-hidden rounded-lg">
              <ExploreLessonCard
                id={coach.id}
                slug={coach.username || coach.id}
                photoUrl={getProfileImageUrlOrPlaceholder({
                  path: coach.profileImagePath || '',
                  placeholder: EMPTY_COACH_AVATAR_SRC,
                })}
                certificationImages={[]}
                fullName={coach.fullName || ''}
                price={coach.priceUnitAmountCoachDefault || 0}
                distance={null}
                currency={coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD}
                defaultSport={coach.defaultSport || undefined}
                isProfessionalRacquetRegistryActive={
                  coach.isProfessionalRacquetRegistryActive || false
                }
                coachSports={coach.coachSports as CoachSports[]}
                reviews={{
                  averageRating: coach.coachReviewsAggregate?.aggregate?.avg?.rating || 0,
                  totalReviews: coach.coachReviewsAggregate?.aggregate?.count || 0,
                }}
                lessonGiven={coach.coachLessonsAggregate?.aggregate?.count || 0}
                activeSport={
                  (coach.defaultSport as SportsEnum) ||
                  (coach.coachSports?.[0]?.sport as SportsEnum) ||
                  SportsEnum.Pickleball
                }
                isPciMember={coach.isPciMember || false}
                impressionSource="CLUB_PAGE"
              />
            </div>
          ))}
        </div>
      )}
        <div className="flex">
          <ButtonLink href={COACH_MARKETPLACE_PAGE} variant="brand" size="md" isInline>
            View all coaches
          </ButtonLink>
        </div>
      </section>
    </>
  );
}
